import {
  Check,
  ChevronDown,
  ExternalLink,
  Link2,
  Maximize2,
  Minimize2,
  RotateCcw,
  Trash2,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  influencerTier,
  type Mention,
} from "@/shared/types/mention";
import { mentionPlatform } from "@/shared/components/mention-platform";
import { hasPlatformLogo, PlatformLogo } from "@/shared/components/PlatformLogo";
import { HighlightedText } from "@/shared/components/HighlightedText";
import { MentionClient } from "@/features/mentions/MentionClient";
import { MentionCenaBadge } from "./MentionCenaBadge";
import { MentionDetailsBody } from "./MentionJustificationsModal";
import { MentionQuickClassify } from "./MentionQuickClassify";
import { useMentionFullText } from "./use-mention-full-text";
import { GenerateSummary, SummaryText } from "./MentionSummary";
import { useMentionSummary } from "./use-mention-summary";
import type { MentionTextView } from "@/shared/vocabulary/mention-text-views";
import { MentionInlineCatalog, type MentionCatalog } from "./MentionInlineCatalog";
import {
  EngagementMetricsList,
  MentionPublisher,
  PressReachLine,
  PublicationTags,
  PublicationTitle,
  ReachOrValue,
  discardLabel,
  editLabel,
  engagementMetrics,
  linkLabel,
  reviewLabel,
} from "./mention-parts";

export type MentionCardProps = {
  mention: Mention;
  selected: boolean;
  expanded: boolean;
  token: string;
  companyLogoUrl?: string | null;
  onToggleSelect: () => void;
  onToggleExpanded: () => void;
  /** Abre a menção no drawer. */
  onOpen: () => void;
  onToggleReviewed: () => void;
  /** Abre o diálogo de "vincular a outra marca" pra esta menção. */
  onLink: () => void;
  /** Descarta (com confirmação) ou restaura, conforme o estado. */
  onDiscard: () => void;
  /** Recarga silenciosa da lista depois de uma gravação no card. */
  onSaved: () => void;
  onError: (message: string) => void;
  /** O catálogo da marca da menção, pro "+" dos temas, mensagens-chave e
   *  porta-vozes. Só é chamado quando alguém abre um "+": o card não faz
   *  chamada nenhuma ao montar. A página guarda a promessa por marca. */
  loadCatalog: (mention: Mention) => Promise<MentionCatalog>;
  /** Só leitura (o Visualizador, 2026-10-22): sem seleção, sem ações, e a
   *  classificação aparece sem gravar. */
  readOnly?: boolean;
  /** Como mostrar o texto (preferência da pessoa, 2026-10-31): a postagem
   *  (o trecho de sempre) ou o resumo da IA. */
  textView?: MentionTextView;
};

/**
 * O card da visão por marca. Saiu da `MentionsPage` em 2026-10-18 (a dona
 * pediu a revisão do card): identidade à esquerda (rede, data, fonte, marca,
 * números), conteúdo no meio — com QUEM PUBLICOU em cima do texto —,
 * classificação à direita; embaixo, temas (com o grupo no chip), mensagens-
 * chave, porta-vozes, etiquetas e as ações.
 *
 * O nome do publicador mora na coluna de conteúdo e não no cabeçalho da
 * identidade: ali ele competia com o logo, a data e o checkbox por 210 px e
 * sobrava largura ZERO (medido no devstack).
 */
export function MentionCard({
  mention,
  selected,
  expanded,
  token,
  companyLogoUrl = null,
  onToggleSelect,
  onToggleExpanded,
  onOpen,
  onToggleReviewed,
  onLink,
  onDiscard,
  onSaved,
  onError,
  loadCatalog,
  readOnly = false,
  textView = "post",
}: MentionCardProps) {
  const tier = influencerTier(mention.followerCount);
  const platform = mentionPlatform(mention);
  const PlatformIcon = platform.Icon;
  const metrics = engagementMetrics(mention);
  const untitled = mention.title === "Publicação sem título";
  const isPress = mention.mediaType === "press";
  // Texto completo no lugar do trecho (2026-10-19): busca o detalhe uma vez
  // por card (`useMentionFullText`) e o guarda, então recolher e abrir de
  // novo não pede de novo. Enquanto carrega, ou se falhar, o trecho fica.
  const fullText = useMentionFullText(mention, token);
  const [wantFull, setWantFull] = useState(false);
  const showingFull = wantFull && fullText.full !== null;
  const text = showingFull && fullText.full ? fullText.full : mention;
  const excerptClass = showingFull ? " is-full" : "";
  useEffect(() => {
    if (fullText.error) {
      setWantFull(false);
      onError(fullText.error);
    }
    // só reage à mensagem nova; `onError` muda a cada render do pai
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullText.error]);
  // O resumo da IA (2026-10-31): só substitui o trecho com a preferência em
  // "resumo" E com resumo na menção; sem ele o trecho fica e, pra quem
  // pode, aparece o "Gerar resumo". "Texto completo" mostra a postagem.
  const summaryState = useMentionSummary(mention, token);
  const wantsSummary = textView === "summary";
  const showingSummary = wantsSummary && !showingFull && summaryState.summary !== null;
  const canGenerate =
    wantsSummary && summaryState.summary === null && !readOnly && !mention.discardedAt;
  const toggleFullText = () => {
    if (showingFull) {
      setWantFull(false);
      return;
    }
    setWantFull(true);
    void fullText.load();
  };
  return (
    <article
      className={`mention-row mention-card${expanded ? " is-expanded" : ""}${selected ? " is-selected" : ""}${mention.discardedAt ? " is-discarded" : ""}`}
      role="listitem"
    >
      <div className="mc-top">
        <div className="mc-identity">
          <div className="mc-identity-head">
            {!readOnly && (
              <label className="mention-row-select">
                <input
                  type="checkbox"
                  checked={selected}
                  onChange={onToggleSelect}
                  aria-label={`Selecionar a menção ${mention.id}`}
                />
              </label>
            )}
            <span
              className={`mention-row-logo net-${platform.tone} mention-data-tooltip`}
              data-tooltip={platform.label}
              role="img"
              aria-label={platform.label}
            >
              {hasPlatformLogo(platform.tone) ? (
                <PlatformLogo tone={platform.tone} size={14} mono />
              ) : (
                <PlatformIcon size={15} aria-hidden="true" />
              )}
            </span>
            <span className="mention-row-date">{mention.date}</span>
          </div>
          {/* O nome da fonte numa linha própria (2026-10-18): na rede o da
              rede, na imprensa o do veículo. */}
          <div className="mc-source" title={mention.source}>
            {mention.source || platform.label}
          </div>
          {mention.brandName && (
            <MentionClient
              brandName={mention.brandName}
              companyName={mention.companyName}
              isCompetitor={mention.isCompetitor}
              logoUrl={mention.isCompetitor ? null : companyLogoUrl}
            />
          )}
          <div className="mc-numbers">
            <div className="mention-row-reach">
              <ReachOrValue
                mediaType={mention.mediaType}
                reach={mention.reach}
                adValue={mention.adValue}
                size={14}
              />
              {tier && <span className="mention-row-tier">{tier}</span>}
            </div>
            {metrics.length > 0 ? (
              <div className="mc-metrics">
                <EngagementMetricsList
                  metrics={metrics}
                  chipClassName="mention-row-metric"
                  iconSize={12}
                />
              </div>
            ) : (
              <PressReachLine
                pressReach={isPress ? mention.pressReach : null}
                className="mc-no-metrics"
                fallback={isPress ? "Sem engajamento (imprensa)" : "Sem engajamento"}
              />
            )}
          </div>
        </div>

        <div className="mc-content">
          <div className="mc-text">
            <MentionPublisher mention={mention} />
            {/* Sem título (quase todo post de rede), a linha some e o trecho
                sobe. O trecho vira o clique que abre a menção. */}
            {!untitled && (
              <div className="mention-row-title">
                <PublicationTitle
                  title={mention.title}
                  spans={mention.highlights.title}
                  onOpen={onOpen}
                />
              </div>
            )}
            {showingSummary && summaryState.summary !== null ? (
              <SummaryText
                summary={summaryState.summary}
                origin={summaryState.origin}
                className={`mention-row-excerpt${untitled ? " is-lead" : ""}`}
              />
            ) : (
              <>
                {untitled ? (
                  <button type="button" className="mention-row-lead" onClick={onOpen}>
                    <p className={`mention-row-excerpt is-lead${excerptClass}`}>
                      <HighlightedText text={text.excerpt} spans={text.highlights.content} />
                    </p>
                  </button>
                ) : (
                  <p className={`mention-row-excerpt${excerptClass}`}>
                    <HighlightedText text={text.excerpt} spans={text.highlights.content} />
                  </p>
                )}
                {canGenerate && <GenerateSummary state={summaryState} />}
              </>
            )}
          </div>
          <div className="mc-content-side">
            {mention.previewImageUrl && (
              <img
                className="mc-thumb"
                src={mention.previewImageUrl}
                alt=""
                loading="lazy"
                onError={(event) => {
                  event.currentTarget.style.display = "none";
                }}
              />
            )}
            <div className="mc-content-actions">
              <button
                type="button"
                className="mention-row-expand"
                aria-expanded={expanded}
                onClick={onToggleExpanded}
              >
                <span>{expanded ? "Menos detalhes" : "Mais detalhes"}</span>
                <ChevronDown size={14} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="mention-row-expand"
                aria-pressed={showingFull}
                disabled={fullText.loading}
                onClick={toggleFullText}
              >
                <span>
                  {fullText.loading ? "Carregando…" : showingFull ? "Recolher texto" : "Texto completo"}
                </span>
                {showingFull ? (
                  <Minimize2 size={14} aria-hidden="true" />
                ) : (
                  <Maximize2 size={14} aria-hidden="true" />
                )}
              </button>
              {mention.link && (
                <a
                  className="mc-open-link"
                  href={mention.link}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Abrir conteúdo de ${mention.source}`}
                >
                  <ExternalLink size={13} aria-hidden="true" />
                  <span>Abrir conteúdo</span>
                </a>
              )}
            </div>
          </div>
        </div>

        <aside className="mc-classify">
          <div className="mc-classify-head">
            <span className="mc-caption">Classificação</span>
            <MentionCenaBadge mention={mention} onToggleExpanded={onToggleExpanded} />
          </div>
          <div className="mention-row-classification">
            <MentionQuickClassify
              mention={mention}
              token={token}
              onSaved={onSaved}
              onError={onError}
              readOnly={readOnly}
            />
          </div>
        </aside>
      </div>

      <div className="mc-meta">
        {(
          [
            ["themes", "Subtemas", mention.themes],
            ["keyMessages", "Mensagens-chave", mention.keyMessages],
            ["spokespersons", "Porta-vozes", mention.spokespersons],
          ] as const
        ).map(([kind, label, items]) => (
          <div className="mc-meta-field" key={kind}>
            <span className="mc-meta-label">{label}</span>
            <MentionInlineCatalog
              kind={kind}
              mention={mention}
              token={token}
              items={items}
              loadOptions={() => loadCatalog(mention).then((catalog) => catalog[kind])}
              onSaved={onSaved}
              onError={onError}
              locked={mention.discardedAt !== null}
              readOnly={readOnly}
            />
          </div>
        ))}
        <div className="mc-meta-field mc-meta-tags">
          <span className="mc-meta-label">Etiquetas</span>
          <PublicationTags
            theme={mention.theme}
            code={mention.nativeId}
            mediaType={mention.mediaType}
            reports={mention.reports}
            otherBrands={mention.otherBrands}
            discarded={discardLabel(mention)}
            reviewed={reviewLabel(mention)}
            edited={editLabel(mention)}
            linked={linkLabel(mention)}
            competitor={mention.isCompetitor ? mention.companyName : null}
            link={null}
            source={mention.source}
            className="mc-chips mc-tag-list"
            showOriginFallback={false}
          />
        </div>
        {!readOnly && <div className="mc-actions" role="group" aria-label="Ações da menção">
          {!mention.discardedAt && (
            <>
              <button
                type="button"
                className={`mc-action mention-data-tooltip${mention.reviewedAt ? " is-active" : ""}`}
                data-tooltip={mention.reviewedAt ? "Desmarcar revisão" : "Marcar como revisada"}
                aria-label={mention.reviewedAt ? "Desmarcar revisão" : "Marcar como revisada"}
                aria-pressed={Boolean(mention.reviewedAt)}
                onClick={onToggleReviewed}
              >
                <Check size={15} aria-hidden="true" />
              </button>
              <button
                type="button"
                className="mc-action mention-data-tooltip"
                data-tooltip="Vincular a outra marca"
                aria-label="Vincular a outra marca"
                onClick={onLink}
              >
                <Link2 size={15} aria-hidden="true" />
              </button>
            </>
          )}
          <button
            type="button"
            className={`mc-action mention-data-tooltip${mention.discardedAt ? "" : " is-danger"}`}
            data-tooltip={mention.discardedAt ? "Restaurar" : "Descartar"}
            aria-label={mention.discardedAt ? "Restaurar" : "Descartar"}
            onClick={onDiscard}
          >
            {mention.discardedAt ? (
              <RotateCcw size={15} aria-hidden="true" />
            ) : (
              <Trash2 size={15} aria-hidden="true" />
            )}
          </button>
        </div>}
      </div>

      {expanded && (
        <div className="mention-row-details">
          <MentionDetailsBody mention={mention} token={token} showClassification={false} />
        </div>
      )}
    </article>
  );
}
